import React from "react";
import _get from "lodash/get";
import _includes from "lodash/includes";
import { Box, Button, Divider, Link as MuiLink } from "@mui/material";
import { Customer } from "@/src/types/Entities/Customer";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { getCaseLink } from "@/src/services/productService";
import { customerHasConsent, getCustomerConsent } from "@/src/services/customerService";
import dayjs from "dayjs";
import { Link } from "react-router";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import Grid from "@mui/material/Grid";
import { ArrowForward } from "@mui/icons-material";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird";

type CalendlyAppointmentProps = {
  product: AbstractCase;
  customer: Customer;
  type: "advisoryAppointment" | "lawyerCounselingCall" | "callbackAppointment";
};

const CalendlyAppointment = ({ product, customer, type }: CalendlyAppointmentProps) => {
  const calendlyData = _get(product, type);

  if (!_get(calendlyData, "appointment") || !customer || _includes(["processed", "not_processed"], _get(calendlyData, "status"))) {
    return null;
  }

  const getConsentUrl = () => {
    return getCaseLink(product) + "/zustimmung_gespraechsaufzeichnung";
  };

  return (
    <ContentBox headline={"Mein Beratungstermin"}>
      <Typography sx={{ marginBottom: "1rem" }}>
        Sie haben einen Termin für den <strong>{dayjs(calendlyData.appointment.date).format("DD.MM.YYYY")}</strong> um{" "}
        <strong>{dayjs(calendlyData.appointment.date).format("HH:mm")}</strong> Uhr gebucht und werden unter der Nummer {customer.telephone} angerufen.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button variant={"outlined"} component={MuiLink} href={calendlyData.rescheduleUrl} target="_blank" color={"primary"} fullWidth>
            Termin verschieben
          </Button>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button variant={"outlined"} component={MuiLink} href={calendlyData.cancelUrl} target="_blank" color={"primary"} fullWidth>
            Termin absagen
          </Button>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button variant={"outlined"} component={Link} to={"/servicewelt/mein-konto#personalData"} color={"primary"} fullWidth>
            Telefonnummer ändern
          </Button>
        </Grid>
      </Grid>
      {calendlyData["phoneCallEnabled"] === true && (
        <>
          <Divider sx={{ margin: "1rem 0" }} />
          <Typography variant={"h7"} component={"h3"} sx={{ marginBottom: "0.5rem" }}>
            Persönliche Zusammenfassung
          </Typography>
          {!getCustomerConsent(customer, "callRecordingAndSummary") && (
            <Typography>Wünschen Sie eine Zusammenfassung Ihres Beratungsgesprächs? Dann geben Sie uns hier Ihre Einverständniserklärung ab.</Typography>
          )}
          {getCustomerConsent(customer, "callRecordingAndSummary") && customerHasConsent(customer, "callRecordingAndSummary") && (
            <Typography>
              Sie erhalten eine automatische Zusammenfassung dieses Gesprächs und haben hierzu dem Mitschnitt von Beratungsgesprächen zugestimmt. Hier können
              Sie Ihr Einverständnis jederzeit widerrufen:
            </Typography>
          )}
          {getCustomerConsent(customer, "callRecordingAndSummary") && !customerHasConsent(customer, "callRecordingAndSummary") && (
            <Typography>
              Sie erhalten <strong>keine</strong> automatische Zusammenfassung dieses Gesprächs, weil Sie dem Mitschnitt von Beratungsgesprächen widersprochen
              haben. Hier können Sie Ihr Einverständnis jederzeit erteilen:
            </Typography>
          )}
          <Box sx={{ marginTop: "0.5rem" }}>
            <LinkLegalbird to={getConsentUrl()} standalone>
              Einverständnis verwalten <ArrowForward />
            </LinkLegalbird>
          </Box>
        </>
      )}
    </ContentBox>
  );
};

export default CalendlyAppointment;
